import React, { useState } from 'react';
import { Grid, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Email, Language, Phone } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { BusinessDetailsOut, HolidayCalendar } from '../../api';
import { useLanguage } from '../../utils/GeneralUtils';
import { useAccountData } from '../StorefrontThemeWrapper';
import { PolicyModal } from '../storefrontPage/components/PolicyModal';
import { whatsAppLinkFormatter } from '../utils';

interface StorefrontFooterProps {
  businessDetails?: BusinessDetailsOut;
}

export const StorefrontFooter = ({ businessDetails }: StorefrontFooterProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const language = useLanguage();
  const account = useAccountData();
  const { accountId } = useParams();
  const [policyModal, setPolicyModal] = useState<{ open: boolean; title: string; content: string }>({
    open: false,
    title: '',
    content: '',
  });

  const contact = businessDetails?.contact;
  const isISR = account?.holiday_calendar === HolidayCalendar.ISR;
  const termsPolicy = businessDetails?.terms_policy;
  const privacyPolicy = businessDetails?.privacy_policy;
  const cancellationPolicy = businessDetails?.cancellation_policy;

  const accessibilityStatementContentHe = `<div dir="rtl"><p>הצהרת הנגישות עודכנה בתאריך 01/04/2025</p><p>Monkeybook, פלטפורמה לניהול והזמנת סדנאות וחוויות. אנו פועלים רבות על מנת להנגיש את הארגון ואת אתר האינטרנט שלנו לאנשים עם מוגבלות על מנת לקדם שוויון זכויות ושקיפות כלפי אנשים עם מוגבלות.</p><h3>מהות אתר אינטרנט נגיש</h3><p>אתר אינטרנט נגיש, הינו אתר המאפשר לאדם עם מוגבלות, לגלוש באותה רמת יעילות והנאה כגולשים אחרים, תוך שימוש ביכולות המערכת עליה הוא פועל ובאמצעות טכנולוגיות מסייעות לנגישות.</p><h3>ביצוע התאמות הנגישות באתר האינטרנט</h3><p>התאמות הנגישות באתר בוצעו בהתאם לסימן ג': שירותי האינטרנט בתקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות) התשע"ג 2013, לתקן הישראלי ת"י 5568 המבוסס על הנחיות WCAG 2.0, האתר הונגש לרמה AA ובכפוף לשינויים והתאמות שבוצעו במסמך התקן הישראלי.</p><p>האתר תומך בשימוש בטכנולוגיות מסייעות כגון תוכנות הקראת מסך, בגלישה בעזרת מקלדת על ידי שימוש במקשי ה-Tab וה-Shift+Tab למעבר בין קישורים, מקשי החיצים, מקש ה-Enter לבחירה, מקש ה-Esc ליציאה מתפריטים וחלונות, לחיצה על H או על מספר למעבר בין כותרות.</p><p>האתר נבדק באופן תקופתי כדי להבטיח את תחזוקת נגישות האתר.</p><h3>פרוט הפטורים של האתר</h3><p>ייתכן כי חלקים מסוימים באתר אינם עומדים באופן מלא בדרישות התקן בשל מגבלות טכנולוגיות או תפעוליות, ואנו פועלים לשפרם באופן שוטף.</p><h3>הצהרה על עמידה חלקית בתקן במקרה של תכני צד ג'</h3><p>בדפי האתר ייתכן ותוכן מסוים תלוי בתכנים שאינם של הארגון (תכני צד ג'), ולכן תיתכן עמידה חלקית בתקן עבור תכנים אלו.</p><h3>ישימות מיטבית לנגישות באתר האינטרנט</h3><p>באתר אינטרנט זה, ניתן לגלוש בצורה מיטבית ונגישה באמצעות הדפדפנים הנפוצים ומומלץ להשתמש בדפדפנים הבאים: Chrome / Safari / Edge ובתוכנות קוראות מסך NVDA / VoiceOver.</p><h3>שלבי ההנגשה בהם נמצא האתר</h3><p>האתר נמצא בתהליך שיפור מתמיד של נגישות. אנו מעריכים כי תהליך ההנגשה יימשך באופן שוטף. עד לתאריך זה, ניתן לפנות אלינו לקבלת סיוע חלופי.</p><h3>הסדרי נגישות בארגון</h3><p>הארגון פועל באופן דיגיטלי בלבד ואינו מקבל קהל במשרדים.</p><h3>דרכי פניה לבקשות, תקלות נגישות והצעות לשיפור</h3><p>במידה ומצאתם באתר האינטרנט בעיה בנושא הנגישות או שהנכם זקוקים עזרה, אתם מוזמנים לפנות אלינו דרך רכז הנגישות של הארגון:</p><p>צוות Monkeybook<br/>אחראי נגישות: עידו<br/>אימייל: <a href="mailto:ido@monkeybook.io">ido@monkeybook.io</a></p></div>`;
  const accessibilityStatementContentEn = `<p>Accessibility Statement last updated: 01/04/2025</p><p>Monkeybook is a platform for managing and booking workshops and experiences. We work extensively to make our organization and website accessible to people with disabilities, in order to promote equal rights and transparency towards people with disabilities.</p><h3>What is an Accessible Website?</h3><p>An accessible website is one that allows a person with a disability to browse with the same level of efficiency and enjoyment as other users, using the capabilities of the system they operate and with the assistance of accessibility-supporting technologies.</p><h3>Accessibility Adjustments on the Website</h3><p>The accessibility adjustments on this website were made in accordance with Section C: Internet Services in the Equal Rights for Persons with Disabilities Regulations (Service Accessibility Adjustments) 5773-2013, the Israeli Standard SI 5568 based on WCAG 2.0 guidelines. The website has been made accessible to Level AA, subject to the changes and adjustments made in the Israeli Standard document.</p><p>The website supports the use of assistive technologies such as screen readers, keyboard navigation using Tab and Shift+Tab to move between links, arrow keys, Enter to select, Esc to exit menus and windows, and pressing H or a number to navigate between headings.</p><p>The website is periodically reviewed to ensure ongoing accessibility maintenance.</p><h3>Website Exemptions</h3><p>Certain parts of the website may not fully meet the standard requirements due to technological or operational limitations, and we are continuously working to improve them.</p><h3>Declaration of Partial Compliance for Third-Party Content</h3><p>Some pages on the website may contain content that depends on third-party content not belonging to the organization, and therefore partial compliance with the standard may apply to such content.</p><h3>Optimal Accessibility on the Website</h3><p>This website can be browsed optimally and accessibly using common browsers. We recommend the following browsers: Chrome / Safari / Edge, and screen reader software: NVDA / VoiceOver.</p><h3>Accessibility Progress Status</h3><p>The website is in a continuous process of accessibility improvement. We estimate that the accessibility process will continue on an ongoing basis. Until then, you are welcome to contact us for alternative assistance.</p><h3>Organizational Accessibility Arrangements</h3><p>The organization operates digitally only and does not receive visitors at its offices.</p><h3>Contact for Requests, Accessibility Issues, and Improvement Suggestions</h3><p>If you find an accessibility problem on the website or need help, you are welcome to contact us through the organization's accessibility coordinator:</p><p>Monkeybook Team<br/>Accessibility Officer: Ido<br/>Email: <a href="mailto:ido@monkeybook.io">ido@monkeybook.io</a></p>`;
  const accessibilityPolicy = { content: { he: accessibilityStatementContentHe, en: accessibilityStatementContentEn } };

  const handlePolicyClick = (title: string, content: string) => {
    setPolicyModal({ open: true, title, content });
  };

  const renderPolicyLink = (
    policy: { external_url?: string; content?: { en?: string; he?: string } } | undefined,
    label: string,
  ) => {
    if (!policy) return null;
    if (policy.external_url) {
      return (
        <Link
          href={policy.external_url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          <Typography variant="body2">{label}</Typography>
        </Link>
      );
    }
    if (policy.content?.[language]) {
      return (
        <Link
          component="button"
          onClick={() => handlePolicyClick(label, policy.content?.[language] || '')}
          sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          <Typography variant="body2">{label}</Typography>
        </Link>
      );
    }
    return null;
  };

  const monkeybookUrl = `https://www.monkeybook.io${
    language === 'he' ? '/he' : ''
  }?utm_source=storefront&utm_medium=footer&utm_campaign=${accountId}`;

  return (
    <>
      <Grid
        container
        flexDirection={isSmallScreen ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isSmallScreen ? 'center' : 'flex-start'}
        gap={isSmallScreen ? 4 : 0}
        sx={{
          borderTop: '1px solid #eee',
          paddingTop: isSmallScreen ? 4 : 6,
          paddingBottom: isSmallScreen ? 4 : 6,
        }}>
        <Grid
          container
          flexDirection="column"
          gap={1}
          width="auto"
          alignItems={isSmallScreen ? 'center' : 'flex-start'}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: 1 }}>
            {account?.name}
          </Typography>
          {contact?.email && (
            <Grid container alignItems="center" gap={1} width="auto">
              <Email sx={{ fontSize: 18, color: 'primary.main' }} />
              <Link href={`mailto:${contact.email}`} sx={{ color: 'text.secondary', textDecoration: 'none' }}>
                <Typography variant="body2">{contact.email}</Typography>
              </Link>
            </Grid>
          )}
          {contact?.phone && isISR && (
            <Grid container alignItems="center" gap={1} width="auto">
              <img src="/images/whatsapp.png" alt="WhatsApp" style={{ width: 18, height: 18 }} />
              <Link
                href={whatsAppLinkFormatter(contact.phone, '')}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'text.secondary', textDecoration: 'none' }}>
                <Typography variant="body2">{contact.phone}</Typography>
              </Link>
            </Grid>
          )}
          {contact?.phone && !isISR && (
            <Grid container alignItems="center" gap={1} width="auto">
              <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Link href={`tel:${contact.phone}`} sx={{ color: 'text.secondary', textDecoration: 'none' }}>
                <Typography variant="body2">{contact.phone}</Typography>
              </Link>
            </Grid>
          )}
          {contact?.instagram && (
            <Grid container alignItems="center" gap={1} width="auto">
              <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FEDA75" />
                    <stop offset="25%" stopColor="#FA7E1E" />
                    <stop offset="50%" stopColor="#D62976" />
                    <stop offset="75%" stopColor="#962FBF" />
                    <stop offset="100%" stopColor="#4F5BD5" />
                  </linearGradient>
                </defs>
                <path
                  fill="url(#instagram-gradient)"
                  d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5M12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10m0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"
                />
              </svg>
              <Link
                href={`https://instagram.com/${contact.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'text.secondary', textDecoration: 'none' }}>
                <Typography variant="body2">@{contact.instagram.replace('@', '')}</Typography>
              </Link>
            </Grid>
          )}
          {contact?.website && (
            <Grid container alignItems="center" gap={1} width="auto">
              <Language sx={{ fontSize: 18, color: '#4285F4' }} />
              <Link
                href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'text.secondary', textDecoration: 'none' }}>
                <Typography variant="body2">{contact.website}</Typography>
              </Link>
            </Grid>
          )}
        </Grid>

        <Grid container flexDirection="column" gap={1} width="auto" alignItems={isSmallScreen ? 'center' : 'flex-end'}>
          {renderPolicyLink(termsPolicy, t('storefront.termsOfService'))}
          {renderPolicyLink(privacyPolicy, t('storefront.privacyPolicy'))}
          {renderPolicyLink(cancellationPolicy, t('storefront.cancellationPolicy'))}
          {isISR && renderPolicyLink(accessibilityPolicy, t('storefront.accessibilityStatement'))}
        </Grid>
      </Grid>

      <Grid container justifyContent="center" pb={3}>
        <Grid container alignItems="center" justifyContent="center" gap={0.5} width="auto">
          <Typography variant="body2" color="text.secondary">
            {t('storefront.sellTicketsWith')}
          </Typography>
          <a href={monkeybookUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center' }}>
            <img alt="Monkeybook" style={{ height: 18 }} src={'/images/logos/monkeybook-light.png'} />
          </a>
        </Grid>
      </Grid>

      <PolicyModal
        open={policyModal.open}
        title={policyModal.title}
        content={policyModal.content}
        onClose={() => setPolicyModal({ ...policyModal, open: false })}
      />
    </>
  );
};
